import * as Types from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { mediaSmallOrTiny } from '@xing-com/layout-tokens';

import { useJobCardTracking } from '../../cards/job-card/use-job-card-tracking';
import { useSearchAndShuffleContext } from '../../context';
import { CardData } from '../../types';
import { useActionsHelper } from '../../use-actions-helper';

import { useBookmarkActions } from './use-bookmark-actions';
import { useCardInteractedLoggedOut } from './use-card-interacted-logged-out';
import { useHandleInteraction } from './use-handle-interaction';
import { useSavedCardActions } from './use-saved-cards-actions';
import { useSearchAlertActions } from './use-search-alert-actions';

type UseJobCardActionsReturn = {
  jobCardSwiping: (
    deckIndex: number,
    actionType: 'left' | 'right' | 'super'
  ) => void;
  jobCardDislike: (deckIndex: number) => void;
  jobCardLike: (deckIndex: number) => void;
  jobCardBookmark: (deckIndex: number) => void;
  jobCardCreateAlert: (deckIndex: number) => void;
  jobCardDeleteBookmark: (jobId: string, itemUrn: string) => void;
  jobCardDeleteSearchAlert: (
    jobId: string,
    searchAlertId: string,
    itemUrn: string
  ) => void;
  availableCardClickHandler: (data: CardData, deckIndex: number) => void;
  takenCardClickHandler: (data: CardData, deckIndex: number) => void;
};

export const useJobCardActions = (): UseJobCardActionsReturn => {
  const { trackOpenJobAd } = useJobCardTracking();
  const { createBookmark, deleteBookmark } = useBookmarkActions();
  const { createAlert, deleteAlert } = useSearchAlertActions();
  const { saveOnYourJobs, updateYourJobsWithAlertId, removeSavedCard } =
    useSavedCardActions();
  const { handleInteraction } = useHandleInteraction();
  const { decks, setStatusBanner } = useSearchAndShuffleContext();
  const { setSwipeAction, removeCardFromDeck } = useActionsHelper();
  const { saveLoggedOutInteraction } = useCardInteractedLoggedOut();
  const { isLoggedOut } = useLoginState();
  const isMobile = useMatchMedia(mediaSmallOrTiny); // be careful: during SSR it will be always true

  const jobCardDislike = (deckIndex: number): void => {
    setSwipeAction(deckIndex, 'left');

    const card = decks[deckIndex].cards[0];

    if (
      isLoggedOut &&
      (card.type === 'AvailableJob' || card.type === 'TakenJob')
    ) {
      saveLoggedOutInteraction(card);
    }
  };

  const jobCardLike = (deckIndex: number): void => {
    setSwipeAction(deckIndex, 'right');

    const card = decks[deckIndex].cards[0];

    if (
      isLoggedOut &&
      (card.type === 'AvailableJob' || card.type === 'TakenJob')
    ) {
      saveLoggedOutInteraction(card);
    }
  };

  const jobCardBookmark = (deckIndex: number): void => {
    setSwipeAction(deckIndex, 'super');

    const card = decks[deckIndex].cards[0];

    if (
      isLoggedOut &&
      (card.type === 'AvailableJob' || card.type === 'TakenJob')
    ) {
      saveLoggedOutInteraction(card);
    }
  };

  const jobCardCreateAlert = (deckIndex: number): void => {
    setSwipeAction(deckIndex, 'super');
  };

  const jobCardDeleteBookmark = async (
    jobId: string,
    itemUrn: string
  ): Promise<void> => {
    const success = await deleteBookmark(jobId, itemUrn);
    if (success) {
      removeSavedCard(jobId);
    }
  };

  const jobCardDeleteSearchAlert = async (
    jobId: string,
    searchAlertId: string,
    itemUrn: string
  ): Promise<void> => {
    const success = await deleteAlert(searchAlertId, itemUrn);
    if (success) {
      removeSavedCard(jobId);
    }
  };

  const jobCardSwiping = async (
    deckIndex: number,
    actionType: 'left' | 'right' | 'super'
  ): Promise<void> => {
    let alertId: string | undefined;
    const card = decks[deckIndex].cards[0];
    if (card.type !== 'AvailableJob' && card.type !== 'TakenJob') {
      return;
    }
    // this is to prevent the next card (that should be fenced) from being shown
    // before the interrupter card is shown
    const interactionCount =
      decks[deckIndex].loadedCount - decks[deckIndex].cards.length;
    if (interactionCount >= (isMobile ? 4 : 2) && isLoggedOut) {
      removeCardFromDeck(deckIndex, 0);
      return;
    }

    setTimeout(() => {
      removeCardFromDeck(deckIndex, 0);
    }, 500);

    if (actionType === 'right') {
      handleInteraction(card, deckIndex, Types.DreamJobInteractionType.Like);
    } else if (actionType === 'left') {
      handleInteraction(card, deckIndex, Types.DreamJobInteractionType.Dislike);
    } else if (actionType === 'super') {
      saveOnYourJobs(card);
      handleInteraction(card, deckIndex, Types.DreamJobInteractionType.Super);

      if (decks[deckIndex]?.cards[0].type === 'AvailableJob') {
        createBookmark(deckIndex);
      } else if (
        decks[deckIndex]?.cards[0].type === 'TakenJob' &&
        !isLoggedOut
      ) {
        alertId = await createAlert(decks[deckIndex]?.cards[0].data, deckIndex);
        if (card.data.id && alertId) {
          updateYourJobsWithAlertId(card.data.id, alertId);
        }
      } else if (
        decks[deckIndex]?.cards[0].type === 'TakenJob' &&
        isLoggedOut
      ) {
        setStatusBanner({
          show: true,
          key: 'SEARCH_AND_SHUFFLE_STATUS_BANNER_LIKE_LOGGED_OUT',
          type: 'success',
          autohide: false,
        });
      }
    }

    if (isLoggedOut) {
      saveLoggedOutInteraction(card);
    }
  };

  const availableCardClickHandler = (
    cardData: CardData,
    deckIndex: number
  ): void => {
    if (cardData.type === 'AvailableJob') {
      window.open(`/jobs/${cardData.data.jobSlug}`, '_blank');
      trackOpenJobAd({
        itemUrn: cardData.data.urn || undefined,
        trackingToken: cardData.data.trackingToken || undefined,
        status: cardData.data.type,
        cardType: 'job_reco',
        position: deckIndex || -1,
      });
    }
  };

  const takenCardClickHandler = (cardData: CardData): void => {
    if (cardData.type === 'TakenJob') {
      const members = cardData.data.similarMembers;
      const pageName = members?.[0]?.user?.pageName;
      const url = pageName
        ? `/profile/${pageName}?sc_o=jobs_dream_job_reco_profile_other_visit&sc_o_PropActionOrigin=jobs_dream_job_status_taken`
        : undefined;

      if (url) {
        window.open(url, '_blank');
      }
    }
  };

  return {
    jobCardSwiping,
    jobCardDislike,
    jobCardLike,
    jobCardBookmark,
    jobCardCreateAlert,
    jobCardDeleteBookmark,
    jobCardDeleteSearchAlert,
    availableCardClickHandler,
    takenCardClickHandler,
  };
};
